import clsx from "clsx";
import {
  applyPhoneEdit,
  formatIndonesianPhone,
  generateId,
} from "../../../Utils";
import FormLabel from "./FormLabel";
import InputError from "./InputError";
import { useTranslation } from "react-i18next";

// The one place the +62 phone field lives. Every screen used to carry its own
// copy of a react-number-format mask, so none of them normalised what was
// typed and 081211112222 was stored as +62081211112222. The mask is gone: it
// kept internal state that a correction could not always reach, and it read the
// "+62" it had printed itself back as part of the number.
//
// Label, hint and error are optional so the older pages that bring their own
// markup (and their own className) can drop this in without changing how they
// look. onChange hands back the finished string, not an event — which is what
// the handleFieldChange helpers in the v2 modals already accept.
const FormPhoneInput = ({
  label,
  required,
  hint,
  id: propId,
  name,
  error,
  value,
  onChange,
  className,
  ...props
}) => {
  const { t } = useTranslation();

  const id = propId || name || generateId();

  const hasError =
    error?.messages?.length ||
    error?.message ||
    (Array.isArray(error) ? error.length : error);

  const inputClass = clsx(
    "block w-full rounded-md shadow-sm text-sm text-gray-900 placeholder:text-gray-400 border-0 ring-1 py-2 focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200",
    hasError
      ? "ring-red-300 focus:ring-red-500"
      : "ring-gray-300 focus:ring-blue-500",
  );

  return (
    <div>
      {Boolean(label) && (
        <FormLabel htmlFor={id} required={required} hint={hint}>
          {t(label)}
        </FormLabel>
      )}
      <div className={clsx("relative", Boolean(label) ? "mt-1" : "")}>
        <input
          id={id}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          className={className || inputClass}
          aria-invalid={hasError}
          aria-errormessage={hasError ? `${id}-error` : undefined}
          aria-describedby={Boolean(hint) ? `${id}-hint` : undefined}
          // Fully controlled on the normalised string, so a correction always
          // reaches the field rather than sitting behind an input's own state.
          value={formatIndonesianPhone(value)}
          onChange={(event) => onChange?.(applyPhoneEdit(event.target.value))}
          {...props}
        />
      </div>
      {hasError && <InputError id={id} error={error} />}
    </div>
  );
};

export default FormPhoneInput;
